import React, { useState, useEffect } from 'react';
import { DollarSign, AlertTriangle, Save, RotateCcw, Eye, EyeOff, CheckCircle, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const PaymentInfoManager = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [shortSummary, setShortSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalData, setOriginalData] = useState({});

  useEffect(() => {
    fetchPaymentInfo();
  }, []);

  useEffect(() => {
    // Check for unsaved changes
    const changed = 
      title !== originalData.title || 
      content !== originalData.content || 
      shortSummary !== originalData.shortSummary;
    setHasChanges(changed);
  }, [title, content, shortSummary, originalData]);

  const fetchPaymentInfo = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/payment-info`);
      if (response.ok) {
        const data = await response.json();
        setTitle(data.title || '');
        setContent(data.content || '');
        setShortSummary(data.short_summary || '');
        setLastUpdated(data.updated_at);
        setOriginalData({
          title: data.title || '',
          content: data.content || '',
          shortSummary: data.short_summary || ''
        });
      }
    } catch (error) {
      console.error('Error fetching payment info:', error);
      toast({
        title: 'Error',
        description: 'Failed to load payment information',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Title and content are required',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/payment-info`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: title.trim(),
          content: content.trim(),
          short_summary: shortSummary.trim() || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setLastUpdated(data.updated_at);
        setOriginalData({
          title: title.trim(),
          content: content.trim(),
          shortSummary: shortSummary.trim()
        });
        setHasChanges(false);
        toast({
          title: '✅ Saved Successfully',
          description: 'Payment information has been updated. Changes are now live on user and trainer dashboards.',
        });
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to save changes',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error saving payment info:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while saving',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset to default content? This will overwrite your custom text.')) {
      return;
    }

    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/payment-info/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTitle(data.title);
        setContent(data.content);
        setShortSummary(data.short_summary);
        setLastUpdated(data.updated_at);
        setOriginalData({
          title: data.title,
          content: data.content,
          shortSummary: data.short_summary
        });
        setHasChanges(false);
        toast({
          title: '🔄 Reset Complete',
          description: 'Payment information has been reset to default content.',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to reset content',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error resetting payment info:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while resetting',
        variant: 'destructive'
      });
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-gray-500">Loading payment information settings...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
            <DollarSign className="w-7 h-7 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Payment Information Manager</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Manage the payment notice displayed on user and trainer dashboards</p>
          </div>
        </div>
        {hasChanges && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-full">
            <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-amber-700 dark:text-amber-400">Unsaved Changes</span>
          </div>
        )}
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-300">
              <p className="font-medium mb-1">Where This Content Appears:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-400">
                <li><strong>User Dashboard</strong> - "Payment Info" tab with red border</li>
                <li><strong>Trainer Dashboard</strong> - "Payment Info" tab with red border</li>
              </ul>
              <p className="mt-2">Changes are reflected immediately on both dashboards after saving.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Editor Card */}
      <Card className="border-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Edit Payment Information
          </CardTitle>
          <CardDescription>
            This content is displayed as a dedicated tab on user and trainer dashboards with a red warning border.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-base font-semibold">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="⚠️ Important Payment Information"
              className="text-lg font-semibold"
            />
            <p className="text-xs text-gray-500">Tip: Start with an emoji like ⚠️ for better visibility</p>
          </div>

          {/* Short Summary */}
          <div className="space-y-2">
            <Label htmlFor="shortSummary" className="text-base font-semibold">Short Summary</Label>
            <textarea
              id="shortSummary"
              value={shortSummary}
              onChange={(e) => setShortSummary(e.target.value)}
              placeholder="A brief one-line summary of the payment policy..."
              className="w-full min-h-[80px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-y bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              rows={2}
            />
            <p className="text-xs text-gray-500">This appears in the highlighted summary box at the top</p>
          </div>

          {/* Full Content */}
          <div className="space-y-2">
            <Label htmlFor="content" className="text-base font-semibold">Full Content</Label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Detailed payment information and disclaimer..."
              className="w-full min-h-[400px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-y bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-sm"
              rows={15}
            />
            <p className="text-xs text-gray-500">
              Formatting tips: Use 🔸 for section headers • Use • for bullet points • Use blank lines between sections
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {saving ? (
                <><span className="animate-spin mr-2">⏳</span> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Changes</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? (
                <><EyeOff className="w-4 h-4 mr-2" /> Hide Preview</>
              ) : (
                <><Eye className="w-4 h-4 mr-2" /> Show Preview</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={resetting}
              className="text-gray-600 hover:text-red-600"
            >
              {resetting ? (
                <><span className="animate-spin mr-2">⏳</span> Resetting...</>
              ) : (
                <><RotateCcw className="w-4 h-4 mr-2" /> Reset to Default</>
              )}
            </Button>
          </div>

          {/* Last Updated */}
          {lastUpdated && (
            <p className="text-xs text-gray-500 flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-green-500" />
              Last saved: {new Date(lastUpdated).toLocaleString()}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Preview */}
      {showPreview && (
        <Card className="border-4 border-red-500 shadow-2xl bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 dark:from-red-950/30 dark:via-orange-950/30 dark:to-yellow-950/30">
          <CardHeader className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 text-white rounded-t-sm">
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Live Preview (How users will see it)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* Preview Header */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {title || 'No title set'}
              </h2>
            </div>

            {/* Preview Summary */}
            {shortSummary && (
              <div className="bg-red-100 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded-xl p-4">
                <p className="text-red-800 dark:text-red-300">{shortSummary}</p>
              </div>
            )}

            {/* Preview Content */}
            <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
              {content || 'No content set'}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentInfoManager;
